"use client";

/**
 * components/update-checker.tsx
 *
 * Detects that a newer build is running on the server than the one this
 * page loaded with, and prompts the user to reload — the only way a
 * long-lived, resumed (never reloaded) PWA session can ever pick up a
 * deploy. See "PWA update mechanism" in docs/PROJECT.md.
 *
 * Checks /api/build-info:
 *   - once on mount
 *   - on every return-to-foreground (visibilitychange -> visible) — the
 *     moment a backgrounded/resumed PWA is most likely to be stale
 *   - every 60s while the tab is visible, as a backstop for sessions that
 *     are simply left open and never backgrounded
 *
 * Never auto-reloads. Never interrupts a hold-to-vote / pending vote gesture
 * (lib/vote-gesture.ts) — if a mismatch is found mid-gesture, the popup is
 * deferred to the next check instead of forced over the user's thumb.
 * "Later" dismisses for this build id only; a subsequent newer build will
 * re-prompt even if the user dismissed an earlier one.
 */

import { useEffect, useRef, useState } from "react";
import { clog, flushDiagnosticsAndWait } from "@/lib/client-log";
import { isVoteGestureActive } from "@/lib/vote-gesture";
import { setKnownBuildId } from "@/lib/known-build";

const POLL_MS = 60_000;
const DISMISSED_KEY = "bnc-update-dismissed-build";
// How long the progress bar takes to fill before reload() fires — paced to
// flushDiagnosticsAndWait actually finishing, not just for show.
const RELOAD_ANIMATION_MS = 1500;

interface Props {
  initialBuildId: string;
}

export default function UpdateChecker({ initialBuildId }: Props) {
  const [latestBuildId, setLatestBuildId] = useState<string | null>(null);
  const [visible, setVisible] = useState(false);
  // null when not reloading; 0-100 while the post-tap flush+reload sequence
  // runs. Same approach as live-arena.tsx's hard-refresh flow: the wait
  // isn't decorative, it's how long flushDiagnosticsAndWait needs to get
  // queued clog entries off the device before the page is torn down.
  const [reloadPct, setReloadPct] = useState<number | null>(null);
  const knownBuildIdRef = useRef(initialBuildId);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Publish the build this page was actually rendered with so other client
  // code (collectDiagnostics(), the Profile page's build readout) can read
  // it without needing this component mounted in their own subtree.
  useEffect(() => {
    setKnownBuildId(initialBuildId);
  }, [initialBuildId]);

  useEffect(() => {
    async function check() {
      let data: { buildId?: string };
      try {
        const res = await fetch("/api/build-info", { cache: "no-store" });
        if (!res.ok) return;
        data = await res.json();
      } catch {
        // Offline or transient — just try again next tick
        return;
      }
      if (!data.buildId || data.buildId === knownBuildIdRef.current) return;

      if (isVoteGestureActive()) {
        clog.debug("update-checker", "newer build detected but a vote gesture is active — deferring prompt", {
          current: knownBuildIdRef.current,
          latest: data.buildId,
        });
        return;
      }

      const dismissedFor = localStorage.getItem(DISMISSED_KEY);
      if (dismissedFor === data.buildId) return;

      clog.info("update-checker", "newer build detected", {
        current: knownBuildIdRef.current,
        latest: data.buildId,
      });
      setLatestBuildId(data.buildId);
      setVisible(true);
    }

    check();

    function onVisibilityChange() {
      if (document.visibilityState === "visible") check();
    }
    document.addEventListener("visibilitychange", onVisibilityChange);
    pollTimerRef.current = setInterval(() => {
      if (document.visibilityState === "visible") check();
    }, POLL_MS);

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, []);

  async function handleUpdateNow() {
    clog.info("update-checker", "user tapped Update Now", { latestBuildId });
    setReloadPct(0);

    const flushPromise = flushDiagnosticsAndWait("update-reload").catch((err) => {
      clog.warn("update-checker", "pre-reload flush failed", { error: String(err) });
    });

    const start = Date.now();
    const tick = () => {
      const pct = Math.min(100, ((Date.now() - start) / RELOAD_ANIMATION_MS) * 100);
      setReloadPct(pct);
      if (pct < 100) requestAnimationFrame(tick);
    };
    tick();

    await Promise.all([
      flushPromise,
      new Promise((resolve) => setTimeout(resolve, RELOAD_ANIMATION_MS)),
    ]);
    // NOT window.location.reload() — confirmed via the field-reported "tap
    // Update Now, it just shows Update Available again" loop (June 17-19
    // 2026): iOS standalone PWAs can treat reload() as a same-document no-op
    // (the same trap as goToVote()'s trophy-nav and the SyncBlockingOverlay
    // reconnect button elsewhere in this file's app — see their comments).
    // When that happens the page never actually re-fetches, so the same old
    // JS keeps running with the same stale knownBuildIdRef, and the very
    // next poll/foreground check immediately re-detects the "newer" build
    // and re-shows this prompt — a loop with no visible indication the
    // reload silently did nothing. A distinct URL forces a real navigation.
    try {
      window.location.assign(`${window.location.pathname}?_t=${Date.now()}`);
    } catch {
      window.location.reload();
    }
  }

  function handleLater() {
    if (latestBuildId) localStorage.setItem(DISMISSED_KEY, latestBuildId);
    clog.info("update-checker", "user dismissed update prompt", { latestBuildId });
    setVisible(false);
  }

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
      style={{ background: "rgba(15,5,32,0.85)" }}
    >
      <div
        className="w-full max-w-sm rounded-3xl overflow-hidden shadow-2xl border border-yellow-400/30"
        style={{ background: "linear-gradient(135deg, #1e0b4a, #3b0764)" }}
      >
        {reloadPct !== null ? (
          <div className="px-6 pt-7 pb-7 text-center">
            <div className="text-6xl mb-3 animate-pulse">🔄</div>
            <h2 className="text-2xl font-black text-yellow-300 mb-2">Updating…</h2>
            <p className="text-purple-200 text-sm leading-relaxed mb-4">
              Saving diagnostics and reloading the app.
            </p>
            <div className="w-full bg-white/10 rounded-full h-2 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-purple-400 to-yellow-400 rounded-full"
                style={{ width: `${reloadPct}%`, transition: "width 100ms linear" }}
              />
            </div>
          </div>
        ) : (
          <>
            <div className="px-6 pt-7 pb-5 text-center">
              <div className="text-6xl mb-3">🆕</div>
              <h2 className="text-2xl font-black text-yellow-300 mb-2">New Update Available!</h2>
              <p className="text-purple-200 text-sm leading-relaxed">
                A new version of the Championship app is ready. Reload now to get the
                latest features and fixes.
              </p>
            </div>
            <div className="px-6 pb-6 flex flex-col gap-2.5">
              <button
                onClick={handleUpdateNow}
                className="w-full rounded-2xl bg-gradient-to-r from-yellow-400 to-yellow-300 hover:from-yellow-300 hover:to-yellow-200 text-purple-900 font-black text-base py-3.5 transition-all shadow-lg shadow-yellow-400/20"
                style={{ WebkitUserSelect: "none", userSelect: "none" }}
              >
                Update Now
              </button>
              <button
                onClick={handleLater}
                className="w-full rounded-2xl bg-white/10 hover:bg-white/15 text-purple-200 font-bold text-sm py-3 transition-all"
                style={{ WebkitUserSelect: "none", userSelect: "none" }}
              >
                Later
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
